import { useMemo, useEffect, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';

import {
  useGetOrgInfoQuery,
  useLoadUserQuery,
  useAddUserMutation as useAddLocalUserMutation,
  useDeleteUserMutation as useDeleteLocalUserMutation,
  useLoadUserRecordQuery
} from './redux/databaseView';

import {
  useAddUserMutation as useAddAuthUserMutation,
  useDeleteUserMutation as useDeleteAuthUserMutation
} from './redux/authApi';

const defaultFunctionalAreas = {
  Engineering: false,
  Purchasing: false,
  'General manager': false,
  Commercial: false,
  Production: false
};

const defaultRoles = {
  Administrator: false,
  'User Manager': false,
  'Workflow Manager': false,
  'Form manager': false
};


export function WaitingElement({
  children,
  waiting,
  actionText,
  ...props
}) {
  const style = {
    flex: 1,
    height: '100%',
    display: 'flex',
    ...props.style
  };

  if (props.size) {
    delete style.display;
  }

  return (
    <div style={style}>
      {children}

      {waiting && (
        <div className="dialog-overlay">
          <div className="spinner">
            {actionText || 'Loading'}...
          </div>
        </div>
      )}
    </div>
  );
}


const CheckboxGroup = ({
  label,
  options,
  object,
  updateFn,
  minWidth = 170
}) => {
  return (
    <Box>
      <FormLabel>{label}</FormLabel>

      <Box
        sx={{
          mt: 0.5,
          display: 'grid',

          /*
           * Fill horizontally first.
           * When there is no more room, wrap onto another row.
           */
          gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`,

          columnGap: 2,
          rowGap: 0
        }}
      >
        {options.map((item) => (
          <FormControlLabel
            key={item}
            control={
              <Checkbox
                checked={!!object[item]}
                onChange={(e) =>
                  updateFn(object, item, e.target.checked)
                }
              />
            }
            label={item}
          />
        ))}
      </Box>
    </Box>
  );
};


export function AddUserDialog(props) {
  const {
    onClose,
    open,
    selectedValue
  } = props;

  const [emailError] = useState(false);
  const [emailErrorMessage] = useState('');

  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');

  const [supervisorData, setSupervisorData] = useState([]);
  const [supervisor, setSupervisor] = useState(0);

  const [rolesObject, setRolesObject] = useState(defaultRoles);
  const [areasObject, setAreasObject] = useState(defaultFunctionalAreas);

  const [showUnassigned, setShowUnassigned] = useState(false);


  /*
   * Queries
   */

  const userList = useLoadUserQuery();

  const {
    isFetching: isOrgFetching,
    data: orgData
  } = useGetOrgInfoQuery();

  const {
    data: userData,
    isSuccess: isUserDataLoaded,
    isFetching: isUserFetching
  } = useLoadUserRecordQuery(
    {
      id: selectedValue?.row?.id
    },
    {
      skip: selectedValue == null
    }
  );


  /*
   * Mutations
   */

	const [addLocalUser] = useAddLocalUserMutation();
	const [deleteLocalUser] = useDeleteLocalUserMutation();

	const [addAuthUser] = useAddAuthUserMutation();
	const [deleteAuthUser] = useDeleteAuthUserMutation();

  /*
   * Functional areas come from site settings.
   */

  const defaultAreas = useMemo(() => {
    if (isOrgFetching) {
      return defaultFunctionalAreas;
    }

    if (!orgData?.data?.siteSettings) {
      return defaultFunctionalAreas;
    }

    try {
      const siteSettings = JSON.parse(orgData.data.siteSettings);

      if (!siteSettings?.functionalAreas) {
        return defaultFunctionalAreas;
      }

      const result = {};

      siteSettings.functionalAreas.forEach((item) => {
        result[item.area] = false;
      });

      return result;
    }
    catch (error) {
      console.error(
        'Unable to parse functional areas',
        error
      );

      return defaultFunctionalAreas;
    }
  }, [
    isOrgFetching,
    orgData
  ]);


  /*
   * Load supervisor list.
   */

  useEffect(() => {
    if (!userList.isSuccess) {
      return;
    }

    const data = [
      {
        id: 0,
        name: 'No supervisor'
      },
      ...userList.data.data
    ];

    setSupervisorData(data);
  }, [
    userList.isSuccess,
    userList.data
  ]);


  /*
   * Reset form when creating a new user.
   */

  useEffect(() => {
    if (selectedValue != null) {
      return;
    }

    setSupervisor(0);

    setName('');
    setDisplayName('');
    setEmail('');

    setShowUnassigned(false);

    setAreasObject({
      ...defaultAreas
    });

    setRolesObject({
      ...defaultRoles
    });
  }, [
    selectedValue,
    defaultAreas
  ]);


  /*
   * Load an existing user into the form.
   */

  useEffect(() => 
	{
    if (!isUserDataLoaded) 
      return;
    

    const userInfo = userData.data;

    let loadedSupervisor = userInfo.supervisor;

    if (loadedSupervisor == null ||loadedSupervisor === -1) 
		loadedSupervisor = 0;

    setSupervisor(loadedSupervisor);

    setName(userInfo.name || '');
    setDisplayName(userInfo.displayName || '');
    setEmail(userInfo.email || '');

    setShowUnassigned(userInfo.showUnassigned !== 0);


    /*
     * Responsibilities / areas
     */

    let areasAdded = {};

    try {
      areasAdded =
        userInfo.areas
          ? JSON.parse(userInfo.areas)
          : {};
    }
    catch (error) {
      console.error(
        'Unable to parse user areas',
        error
      );
    }

    const resultingAreas = {};

    Object.keys(defaultAreas).forEach((key) => {
      resultingAreas[key] = !!areasAdded[key];
    });

    setAreasObject(resultingAreas);


    /*
     * System roles
     */

    let rolesAdded = {};

    try {
      if (userInfo.role?.trim()) {
        rolesAdded = JSON.parse(userInfo.role);
      }
    }
    catch (error) {
      console.error(
        'Unable to parse user roles',
        error
      );
    }

    setRolesObject({
      ...defaultRoles,
      ...rolesAdded
    });

  }, [
    userData,
    isUserDataLoaded,
    defaultAreas
  ]);


  /*
   * Helpers
   */

  function updateObject(object, key, value) {
    const replacement = {
      ...object,
      [key]: value
    };

    if (object === areasObject) {
      setAreasObject(replacement);
    }
    else {
      setRolesObject(replacement);
    }
  }


  function handleSupervisorChange(event) {
    setSupervisor(event.target.value);
  }


  async function handleClose(result = null) {
    await onClose(result);
  }


	async function onSubmit()
		{
		const userInfo={
			id:selectedValue?.row?.id,
			name,
			displayName,
			email,
			supervisor
			};

		/*
		 * Create/reactivate the central login identity first.
		 * The central server owns the user ID.
		 */

		const authResult=await addAuthUser(
			{
			email,
			admin:!!rolesObject['Administrator'],
			userManager:!!rolesObject['User Manager']
			}
			).unwrap();

		if (!authResult?.success)
			return;

		userInfo.id=authResult.data;

		/*
		 * Now create/update the corresponding local user.
		 */

		const localResult=await addLocalUser(
			{
			userInfo,

			showUnassigned:
				showUnassigned
					? 1
					: 0,

			areasObject,
			rolesObject
			}
			).unwrap();

		if (!localResult?.success)
			return;

		await handleClose(name);
		}

	async function onDelete()
		{
		const id=selectedValue?.row?.id;

		if (!id)
			return;

		const authResult=await deleteAuthUser(
			{
			id
			}
			).unwrap();

		if (!authResult?.success)
			return;

		const localResult=await deleteLocalUser(
			{
			id
			}
			).unwrap();

		if (!localResult?.success)
			return;

		await handleClose(name);
		}

  /*
   * Derived values
   */

  const waiting =
    (
      selectedValue != null &&
      isUserFetching
    ) ||
    isOrgFetching;

  const areas = Object.keys(areasObject);
  const roles = Object.keys(rolesObject);

  const isEditing = !!selectedValue;

  const title =
    props.title ||
    (
      isEditing
        ? 'Edit user'
        : 'Add user'
    );

  const action =
    props.action ||
    (
      isEditing
        ? 'Save'
        : 'Create'
    );


  return (
    <Dialog
      open={open}
      onClose={() => handleClose(null)}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        {title}
      </DialogTitle>


      <DialogContent dividers>
        <WaitingElement waiting={waiting}>

          <Stack
            spacing={3}
            sx={{
              width: '100%'
            }}
          >

            {/* USER DETAILS */}

            <Box>
              <FormLabel>
                User details
              </FormLabel>

              <Box
                sx={{
                  mt: 1,

                  display: 'grid',

                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr'
                  },

                  gap: 2
                }}
              >

                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) =>
                    setName(e.target.value)
                  }
                  fullWidth
                />


                <TextField
                  label="Display Name"
                  value={displayName}
                  onChange={(e) =>
                    setDisplayName(e.target.value)
                  }
                  fullWidth
                />


                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  error={emailError}
                  helperText={emailErrorMessage}
                  onChange={(e) =>
                    setEmail(e.target.value)
                  }
                  fullWidth
                />


                <FormControl fullWidth>
                  <InputLabel>
                    Supervisor
                  </InputLabel>

                  <Select
                    label="Supervisor"
                    value={supervisor}
                    onChange={handleSupervisorChange}
                  >
                    {supervisorData.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

              </Box>
            </Box>


            {/* USER PREFERENCE */}

            <Box>
              <FormLabel>
                Queue
              </FormLabel>

              <Box sx={{ mt: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showUnassigned}
                      onChange={(e) =>
                        setShowUnassigned(
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="Show unassigned"
                />
              </Box>
            </Box>


            {/* SYSTEM ROLES */}

            <CheckboxGroup
              label="System Roles"
              options={roles}
              object={rolesObject}
              updateFn={updateObject}
              minWidth={190}
            />


            {/* RESPONSIBILITIES */}

            <CheckboxGroup
              label="Responsibilities"
              options={areas}
              object={areasObject}
              updateFn={updateObject}
              minWidth={180}
            />

          </Stack>

        </WaitingElement>
      </DialogContent>


      <DialogActions
        sx={{
          px: 3,
          py: 1.5
        }}
      >

        {isEditing && (
          <Button
            color="error"
            onClick={onDelete}
          >
            Delete
          </Button>
        )}


        <Box sx={{ flex: 1 }} />


        <Button
          onClick={() =>
            handleClose(null)
          }
        >
          Cancel
        </Button>


        <Button
          onClick={onSubmit}
          variant="contained"
        >
          {action}
        </Button>

      </DialogActions>

    </Dialog>
  );
}


export default AddUserDialog;